// Copyright 2017 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { ActivatedLicenseDTO, License, LicenseTransactionDTO, SellableLicense } from "datatypes/SoramaWebApi";
import { Api } from "libraries/WebApi/Api";
import { IPagedRequest, IPageResult, IWebRequestHandler } from "libraries/WebRequestHandler/IWebRequestHandler";
import { PagedRequest } from "libraries/WebRequestHandler/PagedRequest";
import { Repository } from "repositories/Repository";

@autoinject
export class LicenseHolderRepository extends Repository {
    private webRequestHandler: IWebRequestHandler;

    constructor(webRequestHandler: IWebRequestHandler) {
        super();
        this.webRequestHandler = webRequestHandler;
    }

    //#region Get Requests
    public getActivatedLicensesForLicenseHolder(licenseHolderId: string): IPagedRequest<ActivatedLicenseDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<ActivatedLicenseDTO>>(
            Api.licenseHolder + "/" + licenseHolderId + "/ActivatedLicenses"
        );
        return PagedRequest.create<ActivatedLicenseDTO>(request, (lic) => {
            // Convert to actual date objects
            lic.DateEnd = new Date(lic.DateEnd);
            lic.DateStart = new Date(lic.DateStart);
            return lic;
        });
    }

    public getActivatedMembershipLicensesForLicenseHolder(licenseHolderId: string): IPagedRequest<ActivatedLicenseDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<ActivatedLicenseDTO>>(
            Api.licenseHolder + "/" + licenseHolderId + "/ActivatedMembershipLicenses"
        );
        return PagedRequest.create<ActivatedLicenseDTO>(request, (lic) => {
            // Convert to actual date objects
            lic.DateEnd = new Date(lic.DateEnd);
            lic.DateStart = new Date(lic.DateStart);
            return lic;
        });
    }

    public getSellableLicensesForLicenseHolder(licenseHolderId: string): Promise<SellableLicense[]> {
        return this.webRequestHandler
            .createGetRequest<SellableLicense[]>(Api.licenseHolder + "/" + licenseHolderId + "/SellableLicenses")
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getLicenseBuyTransactionsForLicenseHolder(licenseHolderId: string): Promise<LicenseTransactionDTO[]> {
        return this.webRequestHandler
            .createGetRequest<LicenseTransactionDTO[]>(
                Api.licenseHolder + "/" + licenseHolderId + "/LicenseBuyTransactions"
            )
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getLicenseSellTransactionsForLicenseHolder(licenseHolderId: string): Promise<LicenseTransactionDTO[]> {
        return this.webRequestHandler
            .createGetRequest<LicenseTransactionDTO[]>(
                Api.licenseHolder + "/" + licenseHolderId + "/LicenseSellTransactions"
            )
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getSoldLicensesForLicenseHolder(licenseHolderId: string): Promise<License[]> {
        return this.webRequestHandler
            .createGetRequest<License[]>(Api.licenseHolder + "/" + licenseHolderId + "/SoldLicenses")
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getActivatedFeatureLicensesForLicenseHolder(licenseHolderId: string): IPagedRequest<ActivatedLicenseDTO> {
        const request = this.webRequestHandler.createGetRequest<IPageResult<ActivatedLicenseDTO>>(
            Api.licenseHolder + "/" + licenseHolderId + "/ActivatedFeatureLicenses"
        );
        return PagedRequest.create<ActivatedLicenseDTO>(request, (lic) => {
            // Convert to actual date objects
            lic.DateEnd = new Date(lic.DateEnd);
            lic.DateStart = new Date(lic.DateStart);
            return lic;
        });
    }

    public getActivatedLicensesForLicenseHolderAndDevice(
        licenseHolderId: string,
        deviceId: string
    ): Promise<ActivatedLicenseDTO[]> {
        return this.webRequestHandler
            .createGetRequest<ActivatedLicenseDTO[]>(
                Api.licenseHolder + "/" + licenseHolderId + "/ActivatedLicensesForDevice?deviceId=" + deviceId
            )
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public getSellableFeatureLicensesForLicenseHolder(licenseHolderId: string): Promise<SellableLicense[]> {
        return this.webRequestHandler
            .createGetRequest<SellableLicense[]>(Api.licenseHolder + "/" + licenseHolderId + "/SellableFeatureLicenses")
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }

    public async restoreFeatureLicensesForLicenseHolderAndDevice(
        licenseHolderId: string,
        deviceId: string,
        hardwareClock: number
    ): Promise<string[]> {
        return this.webRequestHandler
            .createPostRequest<string[]>(
                Api.licenseHolder + "/" + licenseHolderId + "/RestoreForDevice/" + deviceId,
                hardwareClock
            )
            .send()
            .then((x) => x.content)
            .catch(this.onError);
    }
    //#endregion
}
